import React, { useCallback, useMemo } from "react";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import ProcessorTable from "./ProcessorTable";
import { useTranslation } from "react-i18next";
import Notice from "components/Notice/Notice";
import { useWatch } from "react-hook-form";

const LOCALE_KEY = "page.merchants.addMerchant.";

/**
 * @param {Object} props
 * @param {import("react-hook-form").UseFormReturn} props.formProps
 */
const ProcessorSection = ({ formProps, isLoading, mccDropdownOptions }) => {
  const { t } = useTranslation();

  const handleExclusiveGatewayOnCheck = useCallback((cardType, enabledProcessor, processors) => {
    const { setValue } = formProps;
    setValue(`processors.${cardType}.inUse`, "NONE"); // Logically will always end up as NONE, since options are mutually exclusive

    processors.filter((processor) => processor !== enabledProcessor).forEach((processor) => {
      setValue(`processors.${cardType}.gateways.${processor}.enabled`, false)
    });
  }, [formProps]);

  const amexGatewayEnabled = useWatch({
    name: "processors.AMEX.gateways.AMEX.enabled",
    control: formProps.control,
  });

  const visaTableConfig = useMemo(() => ({
    cardTypeKey: "VISA",
    defaultValues: {
      inUse: "YEDPAY",
    },
    gateways: [
      {
        gatewayKey: "ALLINPAY",
        defaultValues: {
          enabled: true,
          businessPlan: "sme"
        }
      },
      {
        gatewayKey: "FUBON",
        defaultValues: {
          enabled: true,
          businessPlan: "sme"
        }
      },
      {
        gatewayKey: "YEDPAY",
        defaultValues: {
          enabled: true,
          businessPlan: "sme"
        }
      },
    ]
  }), []);

  const masterTableConfig = useMemo(() => ({
    cardTypeKey: "MASTER",
    defaultValues: {
      inUse: "YEDPAY",
    },
    gateways: [
      {
        gatewayKey: "ALLINPAY",
        defaultValues: {
          enabled: true,
          businessPlan: "sme"
        }
      },
      {
        gatewayKey: "FUBON",
        defaultValues: {
          enabled: true,
          businessPlan: "normal"
        }
      },
      {
        gatewayKey: "YEDPAY",
        defaultValues: {
          enabled: true,
          businessPlan: "sme"
        }
      },
    ]
  }), []);

  const unionPayTableConfig = useMemo(() => ({
    cardTypeKey: "UNIONPAY",
    unavailableFields: ["foreignMdr"],
    defaultValues: {
      inUse: "FUBON",
    },
    gateways: [
      {
        gatewayKey: "ALLINPAY",
        unavailableBusinessPlans: ["sme"],
        defaultValues: {
          enabled: true,
          businessPlan: "normal"
        }
      },
      {
        gatewayKey: "FUBON",
        unavailableBusinessPlans: ["sme"],
        defaultValues: {
          enabled: true,
          businessPlan: "normal"
        }
      },
    ],
  }), []);

  const jcbTableConfig = useMemo(() => ({
    cardTypeKey: "JCB",
    unavailableFields: ["foreignMdr"],
    defaultValues: {
      inUse: "YEDPAY",
    },
    gateways: [
      {
        gatewayKey: "ALLINPAY",
        unavailableBusinessPlans: ["sme"],
        defaultValues: {
          enabled: true,
          businessPlan: "normal"
        }
      },
      {
        gatewayKey: "YEDPAY",
        unavailableBusinessPlans: ["sme"],
        defaultValues: {
          enabled: true,
          businessPlan: "normal"
        }
      },
    ]
  }), []);

  const amexTableConfig = useMemo(() => ({
    cardTypeKey: "AMEX",
    unavailableFields: amexGatewayEnabled ? ["domesticMdr", "foreignMdr", "referrerCommission"] : [],
    gateways: [
      {
        gatewayKey: "AMEX_BBMSL",
        defaultValues: {
          businessPlan: "sme"
        },
        // These two processors are mutually exclusive
        specialHandling: {
          enabledOnCheckFn: () => handleExclusiveGatewayOnCheck("AMEX", "AMEX_BBMSL", ["AMEX_BBMSL", "AMEX"])
        }
      },
      {
        gatewayKey: "AMEX",
        unavailableFields: ["businessPlan", "mcc"],
        specialHandling: {
          enabledOnCheckFn: () => {
            handleExclusiveGatewayOnCheck("AMEX", "AMEX", ["AMEX_BBMSL", "AMEX"])
            formProps.setValue("processors.AMEX.domesticMdr", "");
            formProps.setValue("processors.AMEX.foreignMdr", "");
            formProps.setValue("processors.AMEX.referrerCommission", "");
          }
        }
      },
    ]
  }), [amexGatewayEnabled, handleExclusiveGatewayOnCheck]);

  const octopusTableConfig = useMemo(() => ({
    cardTypeKey: "OCTOPUS",
    unavailableFields: ["foreignMdr"],
    gateways: [
      {
        gatewayKey: "OCTOPUS",
        unavailableFields: ["businessPlan", "mcc"],
        // These three processors are mutually exclusive
        specialHandling: {
          enabledOnCheckFn: () => handleExclusiveGatewayOnCheck("OCTOPUS", "OCTOPUS", ["OCTOPUS", "OCTOPUS_BBMSL", "OCTOPUS_HKBN"])
        }
      },
      {
        gatewayKey: "OCTOPUS_BBMSL",
        unavailableFields: ["businessPlan", "mcc"],
        specialHandling: {
          enabledOnCheckFn: () => handleExclusiveGatewayOnCheck("OCTOPUS", "OCTOPUS_BBMSL", ["OCTOPUS", "OCTOPUS_BBMSL", "OCTOPUS_HKBN"])
        }
      },
      {
        gatewayKey: "OCTOPUS_HKBN",
        unavailableFields: ["businessPlan", "mcc"],
        specialHandling: {
          enabledOnCheckFn: () => handleExclusiveGatewayOnCheck("OCTOPUS", "OCTOPUS_HKBN", ["OCTOPUS", "OCTOPUS_BBMSL", "OCTOPUS_HKBN"])
        }
      },
    ]
  }), [handleExclusiveGatewayOnCheck]);

  const weChatTableConfig = useMemo(() => ({
    cardTypeKey: "WECHAT",
    unavailableFields: ["foreignMdr"],
    gateways: [
      {
        gatewayKey: "WECHAT_PAY",
        unavailableFields: ["businessPlan"]
      },
    ]
  }), []);

  const aliPayTableConfig = useMemo(() => ({
    cardTypeKey: "ALIPAY",
    unavailableFields: ["foreignMdr"],
    gateways: [
      {
        gatewayKey: "ALIPAY",
        unavailableFields: ["businessPlan"]
      },
    ]
  }), []);

  const payMeTableConfig = useMemo(() => ({
    cardTypeKey: "PAYME",
    unavailableFields: ["foreignMdr"],
    gateways: [
      {
        gatewayKey: "HSBC",
        unavailableFields: ["businessPlan"]
      },
    ]
  }), []);

  return (
    <FormBuilder.Section innerStyle={{ gap: "32px" }}>
      <ProcessorTable
        formProps={formProps}
        config={visaTableConfig}
        mccOptions={mccDropdownOptions}
        disabled={isLoading}
      />
      <ProcessorTable
        formProps={formProps}
        config={masterTableConfig}
        mccOptions={mccDropdownOptions}
        disabled={isLoading}
      />
      <ProcessorTable
        formProps={formProps}
        config={unionPayTableConfig}
        mccOptions={mccDropdownOptions}
        disabled={isLoading}
      />
      <ProcessorTable
        formProps={formProps}
        config={jcbTableConfig}
        mccOptions={mccDropdownOptions}
        disabled={isLoading}
      />
      <FormBuilder.Section innerStyle={{ gap: "8px" }}>
        <Notice severity="warning">{t(LOCALE_KEY + "notice.amexBbmslOnboard")}</Notice>
        <ProcessorTable
          formProps={formProps}
          config={amexTableConfig}
          mccOptions={mccDropdownOptions}
          disabled={isLoading}
        />
      </FormBuilder.Section>
      <ProcessorTable
        formProps={formProps}
        config={octopusTableConfig}
        mccOptions={mccDropdownOptions}
        disabled={isLoading}
      />
      <ProcessorTable
        formProps={formProps}
        config={weChatTableConfig}
        mccOptions={mccDropdownOptions}
        disabled={isLoading}
      />
      <ProcessorTable
        formProps={formProps}
        config={aliPayTableConfig}
        mccOptions={mccDropdownOptions}
        disabled={isLoading}
      />
      <ProcessorTable
        formProps={formProps}
        config={payMeTableConfig}
        mccOptions={mccDropdownOptions}
        disabled={isLoading}
      />
    </FormBuilder.Section>
  );
};

export default React.memo(ProcessorSection);

